import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, MessageCircle, TrendingUp, Eye } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { ProjectVisual } from "./ProjectVisual";
import { TiltCard } from "./TiltCard";
import { PROJECTS, WHATSAPP_LINK } from "@/data/content";

const FILTERS = [
  { id: "all", label: "Todos", testid: "filter-all" },
  { id: "empresarial", label: "Empresariais", testid: "filter-business" },
  { id: "games", label: "Games & eSports", testid: "filter-games" },
];

export const Portfolio = () => {
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const visible = PROJECTS.filter((p) => filter === "all" || p.category === filter);

  return (
    <section id="projetos" className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
      <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
        <SectionHeading
          number="05"
          label="Portfólio"
          title="Projetos em"
          accent="destaque."
        />
        <Reveal delay={0.15}>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                data-testid={f.testid}
                onClick={() => setFilter(f.id)}
                className={`rounded-full px-5 py-2.5 font-mono-code text-xs font-bold uppercase tracking-widest transition-all duration-300 ${
                  filter === f.id
                    ? "bg-[#E50914] text-white shadow-[0_0_24px_rgba(229,9,20,0.35)]"
                    : "border border-[#2A2A33] text-zinc-400 hover:border-[#E50914]/50 hover:text-zinc-100"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </Reveal>
      </div>

      <motion.div layout className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        <AnimatePresence mode="popLayout">
          {visible.map((project) => (
            <motion.article
              layout
              key={project.id}
              data-testid={`project-card-${project.id}`}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.94 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="group"
            >
              <TiltCard className="card-glow flex h-full flex-col overflow-hidden rounded-2xl border border-[#22222A] bg-[#101014]">
              <button
                data-testid={`project-preview-btn-${project.id}`}
                onClick={() => setSelected(project)}
                className="relative block aspect-[16/10] w-full overflow-hidden text-left"
                aria-label={`Ver detalhes de ${project.title}`}
              >
                <ProjectVisual project={project} />
                <div className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 backdrop-blur-[2px] transition-opacity duration-300 group-hover:opacity-100">
                  <span className="flex items-center gap-2 rounded-full bg-[#E50914] px-5 py-2.5 font-mono-code text-xs font-bold uppercase tracking-widest text-white">
                    <Eye className="h-4 w-4" />
                    Ver detalhes
                  </span>
                </div>
              </button>
              <div className="flex flex-1 flex-col p-6">
                <div className="flex items-center justify-between gap-3">
                  <span className="rounded-full border border-[#E50914]/40 bg-[#E50914]/10 px-3 py-1 font-mono-code text-[10px] uppercase tracking-[0.2em] text-[#FF2A3B]">
                    {project.categoryLabel}
                  </span>
                  <span className="flex items-center gap-1.5 font-mono-code text-[10px] uppercase tracking-wider text-zinc-500">
                    <TrendingUp className="h-3.5 w-3.5 text-[#E50914]" />
                    {project.stat}
                  </span>
                </div>
                <h3 className="mt-4 font-display text-lg font-bold text-zinc-100">{project.title}</h3>
                <p className="mt-1.5 text-sm text-zinc-500">{project.tagline}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {project.stack.map((tech) => (
                    <span
                      key={tech}
                      className="rounded-md bg-[#0A0A0C] px-2.5 py-1 font-mono-code text-[10px] text-zinc-400"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
              </TiltCard>
            </motion.article>
          ))}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="project-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
            onClick={() => setSelected(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.96 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-[#22222A] bg-[#101014]"
            >
              <div className="relative aspect-[16/9]">
                <ProjectVisual project={selected} />
                <button
                  data-testid="project-modal-close"
                  onClick={() => setSelected(null)}
                  className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-[#2A2A33] bg-[#050505]/80 text-zinc-300 backdrop-blur transition-colors hover:border-[#E50914] hover:text-white"
                  aria-label="Fechar"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-8">
                <span className="rounded-full border border-[#E50914]/40 bg-[#E50914]/10 px-3 py-1 font-mono-code text-[10px] uppercase tracking-[0.2em] text-[#FF2A3B]">
                  {selected.categoryLabel}
                </span>
                <h3 className="mt-4 font-display text-2xl font-bold text-zinc-100 sm:text-3xl">
                  {selected.title}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-zinc-400 sm:text-base">
                  {selected.description}
                </p>
                <div className="mt-6 flex items-center gap-2 rounded-xl border border-[#22222A] bg-[#0A0A0C] px-5 py-4">
                  <TrendingUp className="h-5 w-5 shrink-0 text-[#E50914]" />
                  <span className="font-mono-code text-sm text-zinc-200">{selected.stat}</span>
                </div>
                <div className="mt-6 flex flex-wrap gap-2">
                  {selected.stack.map((tech) => (
                    <span
                      key={tech}
                      className="rounded-md bg-[#0A0A0C] px-3 py-1.5 font-mono-code text-xs text-zinc-300"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
                <a
                  data-testid="project-modal-whatsapp-cta"
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-red-glow mt-8 flex items-center justify-center gap-2.5 rounded-full bg-[#E50914] px-8 py-4 font-mono-code text-sm font-bold uppercase tracking-widest text-white"
                >
                  <MessageCircle className="h-4 w-4" />
                  Quero um site assim
                </a>
                <p className="mt-4 text-center font-mono-code text-[10px] uppercase tracking-widest text-zinc-600">
                  Projeto de demonstração — substituível por cases reais
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
